import { FC } from 'react';
import { UncontrolledPopover, PopoverBody } from 'reactstrap';
import { useAppSelector } from '@/store/hooks';
import SvgRender from 'common/components/general/SvgRender';
import { icon } from '@/library/utils/files.ts';
import useTooltipID from 'common/utils/hooks/useTooltipID';
import Tooltip from 'common/components/general/Tooltip';
import FileButton from 'common/components/buttons/FileButton';
import download from 'common/assets/svg/actions/download.svg';
import AttachmentFile from 'common/components/attachments/AttachmentFile';
import { DownloadAttachmentSizeType } from './config';
import { Attachment } from '@/library/types/files';

type AttachmentsMenuProps = {
  downloadHandler: (event: React.MouseEvent<HTMLElement>) => Promise<void>;
  previewAttachmentHandler: (event: React.MouseEvent<HTMLElement>, file: Attachment) => void;
  closeMenu: () => void;
  isDisabled?: boolean;
  attachments: Attachment[];
  popupPlacement?: 'left' | 'right';
  canDownloadAllAttachments: boolean;
  showDownloadAllButton?: boolean;
  size: DownloadAttachmentSizeType;
  isAttachmentMenuOpen: boolean;
  targetId: string;
};

const AttachmentsMenu: FC<AttachmentsMenuProps> = ({
  downloadHandler,
  closeMenu,
  isDisabled,
  attachments,
  popupPlacement,
  canDownloadAllAttachments,
  showDownloadAllButton = true,
  size,
  previewAttachmentHandler,
  isAttachmentMenuOpen,
  targetId
}) => {
  const { tooltipID, avoidRender } = useTooltipID(`attachments-sync-state`);
  const isOnBoard = useAppSelector(state => state.isOnBoard);

  const handleDownload = async (event: React.MouseEvent<HTMLElement>) => {
    await downloadHandler(event);
    closeMenu();
  };

  const handlePreviewAttachment = (event: React.MouseEvent<HTMLElement>, file: Attachment) => {
    previewAttachmentHandler(event, file);
    closeMenu();
  };

  if (avoidRender || !isAttachmentMenuOpen) return null;

  return (
    <UncontrolledPopover
      trigger="legacy"
      placement={popupPlacement === 'right' ? 'right-start' : 'left-start'}
      className="download-attachments-menu"
      innerClassName="border-0 bg-transparent shadow-none"
      popperClassName="border-0"
      boundariesElement={'window' as any}
      target={targetId}
      isOpen={isAttachmentMenuOpen}
      fade={false}
      hideArrow
    >
      <PopoverBody className="p-0 bg-transparent border-0">
        <div className={`d-flex align-items-center download-attachments-menu--${size}`}>
          {showDownloadAllButton ? (
            <FileButton
              className={`text-white cme-4 bg-primary`}
              onClick={handleDownload}
              disabled={isDisabled}
              id={tooltipID}
              size={size}
            >
              <SvgRender
                className="text-white pointer-events-none"
                src={download}
                style={{ width: 12, height: 12 }}
              />
            </FileButton>
          ) : null}

          {attachments.map((file, index) => (
            <AttachmentFile
              key={file.id}
              file={file}
              onClick={handlePreviewAttachment}
              size={size}
              icon={icon(file.mime_type, file.extension)}
              index={index}
              className={''}
            />
          ))}
        </div>

        {showDownloadAllButton && isOnBoard && !canDownloadAllAttachments && (
          <Tooltip target={tooltipID}>Syncing from office still in progress</Tooltip>
        )}
      </PopoverBody>
    </UncontrolledPopover>
  );
};

export default AttachmentsMenu;
